
import { snowflakeApi } from '../utils/api';
import config from '../utils/config';

export interface DashboardCategory {
  id: string;
  name: string;
  dashboards: GuardrailApp[];
}

export interface GuardrailApp {
  Id: number;
  StreamlitAppId: number;
  CategoryId: number;
  PublishedTitle: string;
  PublishedDescription: string;
  PublishedById: string;
  PublishedAt: string;
  UpdatedAt: string;
  ViewCount: number;
  LastViewedAt: string;
  HasThumbnail: boolean;
  CodeId: string;
  PublishedAppGuid: string;
  AppCreatedAt: string;
  CategoryName: string;
  CategoryColor: string;
  CategoryIcon: string;
  FirstName: string;
  LastName: string;
  PublishedByEmail: string;
  // Additional computed fields
  url?: string;
  isDefault?: boolean;
  thumbnailUrl?: string;
  conversation_id?: string;
}

export interface GuardrailResponse {
  apps: GuardrailApp[];
}

class DashboardService {
  private dashboards: GuardrailApp[] = [];
  private categories: DashboardCategory[] = [];

  /**
   * Get all available dashboards for a client
   */
  async getDashboards(clientId?: string, workflowId?: string): Promise<GuardrailApp[]> {
    try {
      // Fetch published apps from Guardrail API using dashboard_tag from app settings
      const response = await snowflakeApi.get('/api/v1/auth/guardrail/published-apps');
      const guardrailResponse: GuardrailResponse = response.data;
      const guardrailApps = guardrailResponse.apps || [];
      
      // Fetch copilot conversations for this workflow if workflowId is provided
      let conversationsByAppId: Record<string, { conversation_id: string; error_message?: string }> = {};
      if (workflowId) {
        try {
          const conversationsResponse = await snowflakeApi.get(`/api/v1/workflow-v2/copilot-conversations/${workflowId}`);
          conversationsByAppId = conversationsResponse.data.by_app_id || {};
          console.log(`📝 Loaded ${Object.keys(conversationsByAppId).length} copilot conversations for workflow ${workflowId}`);
        } catch (convError) {
          // Log but don't fail if conversations can't be fetched
          console.warn('Failed to fetch copilot conversations:', convError);
        }
      }
      
      // Add computed fields to Guardrail apps, merging conversation IDs from workflow
      // Also collect apps that need their conversation IDs saved to the workflow
      const appsToUpsert: Array<{ app_id: string; conversation_id: string }> = [];
      
      const dashboards: GuardrailApp[] = guardrailApps.map((app, index) => {
        // Use conversation_id from workflow if exists, otherwise use the one from Guardrail app
        const workflowConversation = conversationsByAppId[app.PublishedAppGuid];
        const conversation_id = workflowConversation?.conversation_id || app.conversation_id;
        
        // If workflow doesn't have this conversation but the app does, queue it for upsert
        if (workflowId && !workflowConversation && app.conversation_id) {
          appsToUpsert.push({
            app_id: app.PublishedAppGuid,
            conversation_id: app.conversation_id
          });
        }
        
        return {
          ...app,
          conversation_id,
          url: this.buildDashboardUrlFromApp({ ...app, conversation_id }, clientId, workflowId),
          isDefault: index === 0, // First app is default
          thumbnailUrl: app.HasThumbnail ? this.buildThumbnailUrl(app.PublishedAppGuid) : undefined
        };
      });
      
      // Upsert any missing conversation IDs to the workflow (fire and forget)
      if (workflowId && appsToUpsert.length > 0) {
        console.log(`💾 Saving ${appsToUpsert.length} conversation IDs to workflow ${workflowId}`);
        appsToUpsert.forEach(({ app_id, conversation_id }) => {
          this.upsertCopilotConversation(parseInt(workflowId), app_id, conversation_id);
        });
      }

      this.dashboards = dashboards;
      return dashboards;
    } catch (error) {
      console.error('Failed to fetch dashboards from Guardrail:', error);
      throw error;
    }
  }

  /**
   * Get dashboards organized by categories
   */
  async getDashboardCategories(clientId?: string, workflowId?: string): Promise<DashboardCategory[]> {
    const dashboards = await this.getDashboards(clientId, workflowId);
    
    // Group dashboards by their Guardrail category
    const categoryMap = new Map<string, GuardrailApp[]>();
    
    dashboards.forEach(dashboard => {
      const categoryName = dashboard.CategoryName || 'Other';
      if (!categoryMap.has(categoryName)) {
        categoryMap.set(categoryName, []);
      }
      categoryMap.get(categoryName)!.push(dashboard);
    });
    
    // Convert to DashboardCategory format
    const categories: DashboardCategory[] = Array.from(categoryMap.entries()).map(([categoryName, dashboards]) => ({
      id: categoryName.toLowerCase().replace(/\s+/g, '-'),
      name: categoryName,
      dashboards: dashboards
    }));
    
    // Sort categories by name, but put "Data Processing" first if it exists
    categories.sort((a, b) => {
      if (a.name === 'Data Processing') return -1;
      if (b.name === 'Data Processing') return 1;
      return a.name.localeCompare(b.name);
    });
    
    this.categories = categories;
    return categories;
  }



  /**
   * Build dashboard URL from Guardrail app data
   */
  private buildDashboardUrlFromApp(app: GuardrailApp, clientId?: string, workflowId?: string): string {
    // For Streamlit apps, we need to construct the URL based on the Guardrail base URL
    // This would typically be something like: https://guardrail-domain/app/{StreamlitAppId}
    // For now, we'll use a placeholder structure that can be configured
    
    const guardrailBaseUrl = 'https://insights.dacgroup.com';
    // Use PublishedAppGuid instead of CodeId as the app_id
    let url = `${guardrailBaseUrl}/?app_id=${app.PublishedAppGuid}`;
    
    // Get the authguard token from localStorage and append it to the URL
    const authguardToken = typeof window !== 'undefined' ? localStorage.getItem('authguard_access_token') : null;
    if (authguardToken) {
      url += `&token=${encodeURIComponent(authguardToken)}`;
    }
    
    // Add client context as query parameters
    if (clientId) {
      url += `&client_id=${encodeURIComponent(clientId)}`;
    }
    
    // Add workflow_id if provided
    if (workflowId) {
      url += `&workflow_id=${encodeURIComponent(workflowId)}`;
    }
    
    // Add conversation_id from the app data (generated by backend)
    if (app.conversation_id) {
      url += `&conversation_id=${encodeURIComponent(app.conversation_id)}`;
    }
    
    return url;
  }

  /**
   * Build thumbnail URL for a Guardrail app
   */
  private buildThumbnailUrl(publishedAppGuid: string): string {
    const baseUrl = config.api.snowflake;
    const thumbnailUrl = `${baseUrl}/api/v1/auth/guardrail/streamlit-apps/${publishedAppGuid}/thumbnail`;
    console.log(`🖼️ Building thumbnail URL for PublishedAppGuid ${publishedAppGuid}: ${thumbnailUrl}`);
    return thumbnailUrl;
  }

  /**
   * Upsert a copilot conversation for a workflow and app
   * This saves the conversation_id to the database for future use
   */
  async upsertCopilotConversation(
    workflowId: number,
    appId: string,
    conversationExternalId: string
  ): Promise<void> {
    try {
      await snowflakeApi.post('/api/v1/workflow-v2/copilot-conversations', {
        workflow_id: workflowId,
        app_id: appId,
        conversation_external_id: conversationExternalId
      });
      console.log(`✅ Saved conversation ${conversationExternalId} for app ${appId} to workflow ${workflowId}`);
    } catch (error) {
      // Log but don't fail - this is a background save operation
      console.warn(`Failed to save conversation for app ${appId}:`, error);
    }
  }

  /**
   * Get the full copilot conversation history for the current user, a workflow, and a specific app.
   * Returns entries sorted newest-first.
   */
  async getCopilotConversationHistory(
    workflowId: number,
    appId: string
  ): Promise<import('../types').ConversationHistoryItem[]> {
    try {
      const response = await snowflakeApi.get(
        `/api/v1/workflow-v2/copilot-conversations/${workflowId}/history`,
        { params: { app_id: appId } }
      );
      return response.data ?? [];
    } catch (error) {
      console.warn(`Failed to get copilot conversation history for app ${appId}:`, error);
      return [];
    }
  }

}

export const dashboardService = new DashboardService();
